import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Upload, Loader2, RotateCcw } from 'lucide-react';
import { resolveImageUrl } from '@/lib/resolveImageUrl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useSite } from '@/context/SiteContext';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errorMessage';

// Groups of settings for the admin UI
const GROUPS = [
    {
        title: 'Contatti',
        fields: [
            { key: 'email', label: 'Email' },
            { key: 'phone_shop_label', label: 'Etichetta telefono negozio' },
            { key: 'phone_shop_value', label: 'Telefono negozio (visualizzato)' },
            { key: 'phone_shop_tel', label: 'Telefono negozio (formato +39...)' },
            { key: 'phone_mobile_label', label: 'Etichetta mobile' },
            { key: 'phone_mobile_value', label: 'Mobile (visualizzato)' },
            { key: 'phone_mobile_tel', label: 'Mobile (formato +39...)' },
            { key: 'whatsapp_number', label: 'WhatsApp (solo cifre, es. 390296779924)' },
            { key: 'whatsapp_url', label: 'WhatsApp URL (opzionale, sovrascrive numero)' },
        ],
    },
    {
        title: 'Indirizzo',
        fields: [
            { key: 'address_line1', label: 'Indirizzo riga 1' },
            { key: 'address_line2', label: 'Indirizzo riga 2' },
            { key: 'google_maps_url', label: 'Google Maps URL' },
            { key: 'google_maps_embed_src', label: 'Google Maps embed src' },
        ],
    },
    {
        title: 'Social',
        fields: [
            { key: 'instagram_url', label: 'Instagram URL' },
            { key: 'instagram_handle', label: 'Instagram handle (@...)' },
            { key: 'facebook_url', label: 'Facebook URL' },
            { key: 'tiktok_url', label: 'TikTok URL' },
            { key: 'pinterest_url', label: 'Pinterest URL' },
        ],
    },
    {
        title: 'Orari (giorno per giorno — libero testo, es. "9:30–12:00 | 16:00–19:00" o "Chiuso")',
        fields: [
            { key: 'hours_monday', label: 'Lunedì' },
            { key: 'hours_tuesday', label: 'Martedì' },
            { key: 'hours_wednesday', label: 'Mercoledì' },
            { key: 'hours_thursday', label: 'Giovedì' },
            { key: 'hours_friday', label: 'Venerdì' },
            { key: 'hours_saturday', label: 'Sabato' },
            { key: 'hours_sunday', label: 'Domenica' },
        ],
    },
    {
        title: 'Logo — Dimensioni e margini',
        numeric: true,
        fields: [
            { key: 'logo_width_desktop_px', label: 'Larghezza desktop (px, 80–500)' },
            { key: 'logo_max_height_desktop_px', label: 'Altezza massima desktop (px, 30–200)' },
            { key: 'logo_width_mobile_px', label: 'Larghezza mobile (px, 60–300)' },
            { key: 'logo_max_height_mobile_px', label: 'Altezza massima mobile (px, 30–150)' },
            { key: 'logo_width_footer_px', label: 'Larghezza footer (px, 80–400)' },
            { key: 'logo_max_height_footer_px', label: 'Altezza massima footer (px, 30–200)' },
            { key: 'logo_margin_top_px', label: 'Margine sopra header (px)' },
            { key: 'logo_margin_bottom_px', label: 'Margine sotto header (px)' },
            { key: 'logo_margin_x_px', label: 'Margine laterale header (px)' },
        ],
    },
    {
        title: 'Colori (Color Picker + HEX)',
        color: true,
        fields: [
            { key: 'color_primary', label: 'Colore primario (verde)' },
            { key: 'color_secondary', label: 'Colore secondario (avorio)' },
            { key: 'color_background', label: 'Sfondo' },
            { key: 'color_text', label: 'Testo' },
            { key: 'color_muted', label: 'Testo secondario' },
            { key: 'color_border', label: 'Bordi' },
            { key: 'color_accent', label: 'Accento' },
            { key: 'color_card', label: 'Card / superfici' },
        ],
    },
];

const DEFAULT_COLORS = {
    color_primary: '#6e8874',
    color_secondary: '#f8faec',
    color_background: '#f8faec',
    color_text: '#2b241d',
    color_muted: '#8b7d70',
    color_border: '#dad3c1',
    color_accent: '#c98f6a',
    color_card: '#ffffff',
};

export default function SiteSettingsPanel() {
    const { settings, reload } = useSite();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(null); // 'desktop' | 'mobile' | null

    useEffect(() => { setForm(settings || {}); }, [settings]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/admin/site/settings', { settings: form });
            toast.success('Impostazioni salvate. Il sito si aggiornerà a breve.');
            await reload();
        } catch (e) {
            toast.error(errorMessage(e, 'Errore salvataggio'));
        } finally { setSaving(false); }
    };

    const resetColors = () => setForm((f) => ({ ...f, ...DEFAULT_COLORS }));

    const uploadLogo = async (variant, file) => {
        setUploadingLogo(variant);
        try {
            const fd = new FormData();
            fd.append('variant', variant);
            fd.append('file', file);
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/site/logo/upload`, {
                method: 'POST',
                credentials: 'include', // send httpOnly admin cookie
                body: fd,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Upload fallito');
            toast.success(`Logo ${variant} caricato`);
            await reload();
        } catch (e) {
            toast.error(e.message || 'Errore upload');
        } finally { setUploadingLogo(null); }
    };

    const clearLogo = async (variant) => {
        const field = variant === 'desktop' ? 'logo_url_desktop' : 'logo_url_mobile';
        set(field, '');
        try {
            await api.put('/admin/site/settings', { settings: { [field]: '' } });
            toast.success('Logo rimosso (torna al fallback SVG)');
            await reload();
        } catch (e) {
            toast.error(errorMessage(e, 'Errore'));
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-settings-panel">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-serif text-xl">Impostazioni sito</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Contatti, colori, orari, social e logo. Le modifiche si applicano al sito pubblico dopo il salvataggio.</p>
                </div>
                <Button onClick={save} disabled={saving} data-testid="admin-settings-save" className="h-10 px-4">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Salva
                </Button>
            </div>

            {/* Logo uploader */}
            <div className="rounded-xl border border-border bg-card p-5">
                <p className="eyebrow mb-3">Logo</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {['desktop', 'mobile'].map((variant) => {
                        const field = variant === 'desktop' ? 'logo_url_desktop' : 'logo_url_mobile';
                        const url = form[field];
                        return (
                            <div key={variant} className="space-y-2">
                                <Label className="eyebrow capitalize">Logo {variant}</Label>
                                <div className="h-24 rounded-md border border-dashed border-border bg-secondary/40 flex items-center justify-center">
                                    {url ? (
                                        <img src={resolveImageUrl(url)} alt={`Logo ${variant}`} className="max-h-20 max-w-full object-contain" />
                                    ) : (
                                        <span className="text-xs text-muted-foreground">Nessun logo caricato (fallback: FLOREAL SVG)</span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <label className={cn('inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border cursor-pointer text-sm hover:bg-secondary', uploadingLogo === variant && 'opacity-50')}>
                                        {uploadingLogo === variant ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                                        Carica
                                        <input type="file" accept="image/*" hidden disabled={uploadingLogo === variant} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(variant, f); e.target.value = ''; }} />
                                    </label>
                                    {url && (
                                        <Button variant="ghost" size="sm" className="h-9 px-3 text-muted-foreground" onClick={() => clearLogo(variant)}>Rimuovi</Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* All grouped fields */}
            {GROUPS.map((group) => (
                <div key={group.title} className="rounded-xl border border-border bg-card p-5">
                    <div className="flex items-center justify-between mb-4">
                        <p className="eyebrow">{group.title}</p>
                        {group.color && (
                            <Button variant="ghost" size="sm" onClick={resetColors} className="h-8 text-xs">
                                <RotateCcw className="h-3 w-3 mr-1" /> Ripristina colori
                            </Button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {group.fields.map((f) => (
                            <div key={f.key}>
                                <Label htmlFor={`s-${f.key}`} className="eyebrow">{f.label}</Label>
                                {group.color ? (
                                    <div className="flex gap-2 mt-1.5">
                                        <input
                                            id={`s-${f.key}`}
                                            type="color"
                                            data-testid={`admin-settings-input-${f.key}`}
                                            value={form[f.key] || '#000000'}
                                            onChange={(e) => set(f.key, e.target.value)}
                                            className="h-10 w-14 rounded-md border border-border cursor-pointer"
                                        />
                                        <Input
                                            value={form[f.key] || ''}
                                            onChange={(e) => set(f.key, e.target.value)}
                                            className="font-mono"
                                            placeholder="#RRGGBB"
                                        />
                                    </div>
                                ) : group.numeric ? (
                                    <Input
                                        id={`s-${f.key}`}
                                        type="number"
                                        data-testid={`admin-settings-input-${f.key}`}
                                        value={form[f.key] ?? ''}
                                        onChange={(e) => set(f.key, e.target.value === '' ? '' : Number(e.target.value))}
                                        className="mt-1.5"
                                    />
                                ) : (
                                    <Input
                                        id={`s-${f.key}`}
                                        data-testid={`admin-settings-input-${f.key}`}
                                        value={form[f.key] || ''}
                                        onChange={(e) => set(f.key, e.target.value)}
                                        className="mt-1.5"
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            <div className="flex justify-end">
                <Button onClick={save} disabled={saving} className="h-11 px-5">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Salva impostazioni
                </Button>
            </div>
        </div>
    );
}
